import React, { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  LayoutDashboard,
  CheckCircle,
  ListChecks,
  Users,
  Smartphone,
  ClipboardCheck,
  UserCheck,
  Shield,
  FileCheck,
  Package,
  Search,
  Settings,
  LogOut,
  Bell,
  Calendar,
  Map,
  AlertTriangle,
  Clock,
  XCircle
} from 'lucide-react';
import { canAccessPage } from '@/utils/rbac';

const DashboardLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [currentStore, setCurrentStore] = useState(null);
  const [allStores, setAllStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Fetch stores and set current store
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(stores => {
          setAllStores(stores);
          
          // For Network Admin or Regional Manager, use selected store or first store
          if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
            const savedStoreId = localStorage.getItem('selectedStoreId');
            if (savedStoreId && stores.find(s => s.id === savedStoreId)) {
              setSelectedStoreId(savedStoreId);
              setCurrentStore(stores.find(s => s.id === savedStoreId));
            } else if (stores.length > 0) {
              setSelectedStoreId(stores[0].id);
              setCurrentStore(stores[0]);
            }
          } else if (user?.store_id) {
            // For other users, use their assigned store
            const userStore = stores.find(s => s.id === user.store_id);
            if (userStore) {
              setCurrentStore(userStore);
              setSelectedStoreId(userStore.id);
            }
          }
        })
        .catch(console.error);
    }
  }, [user?.store_id, user?.role]);

  // Handle store change for Network Admin
  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = allStores.find(s => s.id === storeId);
    setCurrentStore(store);
    localStorage.setItem('selectedStoreId', storeId);
    
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new CustomEvent('storeChanged', { detail: { storeId } }));
  };

  // Fetch notification data
  useEffect(() => {
    const fetchNotificationData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const [rostersRes, devicesRes, checklistRes] = await Promise.all([
          fetch(`${API}/rosters`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/devices`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/weekly-checklist-progress`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ ok: false }))
        ]);

        const rosters = rostersRes.ok ? await rostersRes.json() : [];
        const devices = devicesRes.ok ? await devicesRes.json() : [];
        let checklistProgress = [];
        if (checklistRes.ok) {
          checklistProgress = await checklistRes.json();
        }

        // Generate smart notifications
        const newNotifications = [];

        // Check roster assignments by day
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];
        const rostersByDay = {};
        days.forEach(day => {
          rostersByDay[day] = rosters.filter(r => r.day === day).length;
        });
        
        const daysWithNoRoster = days.filter(day => rostersByDay[day] === 0);
        if (daysWithNoRoster.length > 0) {
          newNotifications.push({
            id: 'roster-incomplete',
            type: 'warning',
            title: 'Roster Incomplete',
            message: `No team members rostered for: ${daysWithNoRoster.join(', ')}`,
            link: '/roster-areas',
            icon: Users
          });
        }

        // Check total roster count
        const totalRostered = rosters.length;
        if (totalRostered < 10) {
          newNotifications.push({
            id: 'roster-low',
            type: totalRostered < 5 ? 'critical' : 'warning',
            title: 'Low Roster Count',
            message: `Only ${totalRostered} team member${totalRostered !== 1 ? 's' : ''} rostered. Minimum 10 recommended.`,
            link: '/roster-areas',
            icon: AlertTriangle
          });
        }

        // Check device allocations
        const totalDevices = devices.length;
        if (totalDevices < 4) {
          newNotifications.push({
            id: 'devices-low',
            type: 'warning',
            title: 'Device Pool Low',
            message: `Only ${totalDevices} device${totalDevices !== 1 ? 's' : ''} allocated. Minimum 4 required.`,
            link: '/device-pool',
            icon: Smartphone
          });
        }

        // Check unassigned devices
        const unassignedDevices = devices.filter(d => d.status === 'available').length;
        if (unassignedDevices > 0 && totalDevices >= 4) {
          newNotifications.push({
            id: 'devices-unassigned',
            type: 'info',
            title: 'Unassigned Devices',
            message: `${unassignedDevices} device${unassignedDevices !== 1 ? 's' : ''} not yet assigned to team members.`,
            link: '/device-pool',
            icon: Smartphone
          });
        }

        // Check pre-stocktake checklist progress
        const TOTAL_CHECKLIST_TASKS = 30;
        let completedTasks = 0;
        if (Array.isArray(checklistProgress)) {
          checklistProgress.forEach(item => {
            if (item.completed_tasks) {
              completedTasks += Object.keys(item.completed_tasks).length;
            }
          });
        }
        const checklistPercentage = Math.round((completedTasks / TOTAL_CHECKLIST_TASKS) * 100);
        
        if (checklistPercentage < 100) {
          const remainingTasks = TOTAL_CHECKLIST_TASKS - completedTasks;
          newNotifications.push({
            id: 'checklist-incomplete',
            type: checklistPercentage < 50 ? 'warning' : 'info',
            title: 'Pre-Stocktake Checklist',
            message: `${remainingTasks} task${remainingTasks !== 1 ? 's' : ''} remaining (${checklistPercentage}% complete)`,
            link: '/pre-stocktake',
            icon: ClipboardCheck
          });
        }

        // Add success notification if everything is complete
        if (newNotifications.length === 0) {
          newNotifications.push({
            id: 'all-complete',
            type: 'success',
            title: 'All Tasks Complete',
            message: 'Great work! All preparation tasks have been completed.',
            link: '/readiness',
            icon: CheckCircle
          });
        }

        setNotifications(newNotifications);
      } catch (error) {
        console.error('Error fetching notification data:', error);
      }
    };

    fetchNotificationData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNotificationData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getNotificationIconColor = (type) => {
    switch (type) {
      case 'critical':
        return 'text-red-500';
      case 'warning':
        return 'text-amber-500';
      case 'success':
        return 'text-green-500';
      default:
        return 'text-blue-500';
    }
  };

  const criticalCount = notifications.filter(n => n.type === 'critical' || n.type === 'warning').length;

  const isActive = (path) => location.pathname === path;

  const navSections = [
    {
      title: 'OVERVIEW',
      items: [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/readiness', icon: CheckCircle, label: 'Readiness', badge: 'NEW' },
        { path: '/scheduling', icon: Calendar, label: 'Scheduling' }
      ]
    },
    {
      title: 'PREPARATION',
      items: [
        { path: '/pre-stocktake', icon: ListChecks, label: 'Pre-Stocktake', badge: '5W' },
        { path: '/roster-areas', icon: Users, label: 'Roster & Areas' },
        { path: '/device-pool', icon: Smartphone, label: 'Device Pool' }
      ]
    },
    {
      title: 'COUNT WEEK',
      items: [
        { path: '/checklist', icon: ClipboardCheck, label: 'Checklist' },
        { path: '/store-map', icon: Map, label: 'Store Map', badge: 'NEW' },
        { path: '/attendance', icon: UserCheck, label: 'Attendance' },
        { path: '/miscounts', icon: AlertTriangle, label: 'Miscounts' },
        { path: '/verifications', icon: Shield, label: 'Verifications' },
        { path: '/admin-cutoff', icon: FileCheck, label: 'Admin Cut-off' }
      ]
    },
    {
      title: 'INVENTORY',
      items: [
        { path: '/top-stock', icon: Package, label: 'Top Stock' }
      ]
    }
  ];

  const getRoleName = (roleKey) => {
    const roles = {
      none: 'Basic Access',
      stocktake_champion: 'Stocktake Champion',
      stocktake_controller: 'Stocktake Controller',
      stocktake_auditor: 'Stocktake Auditor',
      supervisor: 'Supervisor',
      store_manager: 'Coordinator',
      complex_manager: 'Complex Manager',
      regional_manager: 'Regional Manager',
      network_admin: 'Network Admin'
    };
    return roles[roleKey] || roleKey;
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-slate-900 text-slate-300 border-r border-slate-800">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 3v1H4v2h1v13a2 2 0 002 2h10a2 2 0 002-2V6h1V4h-5V3H9zm0 3h6v11H9V6z"/>
              </svg>
            </div>
            <div>
              <div className="text-white font-bold text-sm">INDRO</div>
              <div className="text-xs text-slate-400">STOCKTAKE MANAGER</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Store Selector for Network Admin and Regional Manager */}
          {(user?.role === 'network_admin' || user?.role === 'regional_manager') && allStores.length > 1 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">
                Viewing Store
              </div>
              <select
                value={selectedStoreId || ''}
                onChange={(e) => handleStoreChange(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="store-selector"
              >
                {allStores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {navSections.map((section) => {
            // Filter items based on user permissions
            const accessibleItems = section.items.filter(item => canAccessPage(user, item.path));
            
            // Don't show section if no items are accessible
            if (accessibleItems.length === 0) return null;
            
            return (
              <div key={section.title}>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
                  {section.title}
                </div>
                <div className="space-y-1">
                  {accessibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          active
                            ? 'bg-teal-600 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                        {item.badge && (
                          <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full font-semibold">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {canAccessPage(user, '/settings') && (
            <div className="border-t border-slate-800 pt-4">
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive('/settings')
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid="nav-settings"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm font-medium">Settings</span>
              </Link>
            </div>
          )}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-800">
          <div className="bg-slate-800 rounded-lg p-3 mb-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 bg-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.name || 'User'}</div>
                <div className="text-xs text-slate-400">ID: {user?.team_member_id}</div>
              </div>
            </div>
            <div className="text-xs text-teal-400 font-medium">{getRoleName(user?.role)}</div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        {/* Top Header Bar */}
        <div className="bg-white border-b border-slate-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">DASHBOARD</h2>
              <p className="text-sm text-slate-500" data-testid="header-store-name">{currentStore?.name || 'Loading...'}</p>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="search-button">
                <Search className="w-5 h-5 text-slate-600" />
              </button>
              
              {/* Notifications Popover */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <button 
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors relative" 
                    data-testid="notifications-button"
                  >
                    <Bell className="w-5 h-5 text-slate-600" />
                    {criticalCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {criticalCount}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-96 p-0 mr-4" 
                  align="end"
                  data-testid="notifications-popover"
                >
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    <p className="text-xs text-slate-500">Tasks requiring attention</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notifications.map((notification) => {
                          const IconComponent = notification.icon;
                          return (
                            <Link
                              key={notification.id}
                              to={notification.link}
                              onClick={() => setNotificationsOpen(false)}
                              className={`block p-4 hover:bg-slate-50 transition-colors border-l-4 ${getNotificationStyle(notification.type)}`}
                              data-testid={`notification-${notification.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <IconComponent className={`w-5 h-5 mt-0.5 flex-shrink-0 ${getNotificationIconColor(notification.type)}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm">{notification.title}</p>
                                  <p className="text-xs mt-0.5 opacity-80">{notification.message}</p>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-slate-200 bg-slate-50">
                    <Link 
                      to="/readiness" 
                      onClick={() => setNotificationsOpen(false)}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                    >
                      View Readiness Dashboard →
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>

              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="profile-button">
                <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
